import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, RefreshCw, LogOut, ArrowLeft, Mail, Phone, Building2, Briefcase, Clock, DollarSign, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Seo } from "@/components/site/Seo";
import { adminVerify, adminLogout, adminListContacts } from "@/lib/api";
import { devLog } from "@/lib/devLog";
import { AdminLoginCard } from "@/components/site/admin/AdminLoginCard";

const formatDateTime = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || "—"; }
};

const Field = ({ label, children, icon: Icon }) => (
  <div className="flex items-start gap-2 text-sm">
    {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />}
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-slate-800">{children || "—"}</p>
    </div>
  </div>
);

const LeadRow = ({ lead, expanded, onToggle }) => (
  <li data-testid={`admin-lead-row-${lead.reference}`} className="border-b border-slate-200 last:border-b-0">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full flex-col items-start gap-2 p-5 text-left transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
      aria-expanded={expanded}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-mono font-semibold text-slate-900">{lead.reference}</span>
          <span>· {formatDateTime(lead.created_at)}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${lead.status === "new" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            {lead.status || "new"}
          </span>
        </div>
        <h3 className="mt-1 truncate text-base font-bold text-slate-900">
          {lead.name} <span className="font-normal text-slate-500">· {lead.company}</span>
        </h3>
        <p className="mt-0.5 truncate text-xs text-slate-500">{lead.email}{lead.role && ` · ${lead.role}`}</p>
      </div>
      <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
    </button>
    {expanded && (
      <div data-testid={`admin-lead-detail-${lead.reference}`} className="border-t border-slate-100 bg-slate-50/50 p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Email" icon={Mail}>
            <a href={`mailto:${lead.email}`} className="text-blue-700 hover:underline">{lead.email}</a>
          </Field>
          <Field label="Phone" icon={Phone}>
            {lead.phone ? <a href={`tel:${lead.phone}`} className="text-blue-700 hover:underline">{lead.phone}</a> : null}
          </Field>
          <Field label="Company" icon={Building2}>{lead.company}</Field>
          <Field label="Role" icon={Briefcase}>{lead.role}</Field>
          <Field label="Preferred contact">{lead.preferred_contact}</Field>
          <Field label="Budget" icon={DollarSign}>{lead.project_budget}</Field>
          <Field label="Timeline" icon={Clock}>{lead.project_timeline}</Field>
          <Field label="Region">{lead.region}</Field>
          <Field label="Source page">{lead.source_page}</Field>
        </div>
        {(lead.industries?.length || 0) > 0 && (
          <div className="mt-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Industry verticals</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {lead.industries.map((i) => (
                <span key={i} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">{i}</span>
              ))}
            </div>
          </div>
        )}
        {(lead.platforms?.length || 0) > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Advertising platforms</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {lead.platforms.map((p) => (
                <span key={p} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">{p}</span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Message</p>
          <p className="mt-1 whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800">
            {lead.message}
          </p>
        </div>
      </div>
    )}
  </li>
);

export default function AdminLeadsPage() {
  const [auth, setAuth] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListContacts({ limit: 500 });
      setLeads(data);
    } catch (e) {
      devLog.error("AdminLeads: load failed", e);
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await adminVerify();
        setAuth(true);
        await load();
      } catch (e) {
        devLog.warn("AdminLeads: not signed in", e);
        setAuth(false);
      }
    })();
  }, []);

  const handleAuthenticated = async () => {
    setAuth(true);
    await load();
  };

  const handleLogout = async () => {
    try { await adminLogout(); } catch (e) { devLog.warn("AdminLeads: logout failed", e); }
    setAuth(false);
    setLeads([]);
    setExpanded(null);
  };

  if (auth !== true) {
    return <AdminLoginCard onAuthenticated={handleAuthenticated} titleKey="leads" />;
  }

  const q = filter.trim().toLowerCase();
  const visibleLeads = q
    ? leads.filter((l) =>
        [l.name, l.email, l.company, l.role, l.reference, l.message]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      )
    : leads;

  return (
    <div data-testid="admin-leads-page">
      <Seo title="Admin · Leads" path="/admin-clan/leads" noindex />
      <section className="mx-auto w-full max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
        <Link to="/admin-clan" className="inline-flex items-center text-sm font-semibold text-blue-700" data-testid="admin-leads-back">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to console
        </Link>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Lead Inbox</div>
            <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">Professional inquiries</h1>
            <p className="mt-1 text-sm text-slate-600">
              Every submission from <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">/contact</code>. Newest first.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              className="h-10 rounded-lg border-slate-300 bg-white"
              data-testid="admin-leads-refresh"
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="h-10 rounded-lg border-slate-300 bg-white"
              data-testid="admin-leads-logout"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>

        <div className="mt-6 max-w-md">
          <Label htmlFor="lead-filter" className="text-xs font-semibold text-slate-700">Filter</Label>
          <Input
            id="lead-filter"
            data-testid="admin-leads-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, company, email, reference…"
            className="mt-1 h-11 rounded-lg border-slate-200"
          />
        </div>

        <p className="mt-4 text-xs text-slate-500" data-testid="admin-leads-count">
          Showing {visibleLeads.length} of {leads.length} {leads.length === 1 ? "lead" : "leads"}
        </p>

        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {visibleLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Inbox className="h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">
                {leads.length === 0 ? "No leads yet." : "No leads match your filter."}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {leads.length === 0
                  ? "Submissions from /contact will appear here automatically."
                  : "Try a different search term."}
              </p>
            </div>
          ) : (
            <ul>
              {visibleLeads.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  expanded={expanded === lead.id}
                  onToggle={() => setExpanded((prev) => (prev === lead.id ? null : lead.id))}
                />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
